/**
 * Service Trips - API Tracking
 * Liste, arrêt, positions des trajets.
 */
import { createTrackingClient } from "../trackingApi";

export type TripStatus = "created" | "started" | "finished";

export interface Trip {
  id: string;
  status: TripStatus;
  bus: { id: string; name: string; plate_number: string };
  route: { id: string; name: string };
  driver_username?: string;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  trip_type?: string;
}

export interface TripPosition {
  id: string;
  latitude: number;
  longitude: number;
  /** API Tracking renvoie recorded_at */
  timestamp?: string;
  recorded_at?: string;
  trip?: string;
}

export interface FetchTripsParams {
  status?: TripStatus;
  bus_id?: string;
  route_id?: string;
  driver_id?: string;
}

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

/**
 * Liste des trips avec filtres optionnels.
 */
export async function fetchTrips(
  accessToken: string | undefined,
  params?: FetchTripsParams
): Promise<Trip[]> {
  const client = getClient(accessToken);
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append("status", params.status);
  if (params?.bus_id) searchParams.append("bus_id", params.bus_id);
  if (params?.route_id) searchParams.append("route_id", params.route_id);
  if (params?.driver_id) searchParams.append("driver_id", params.driver_id);
  const query = searchParams.toString();
  const url = query ? `/api/v1/trips/?${query}` : "/api/v1/trips/";
  const response = await client.get<Trip[] | { results: Trip[] }>(url);
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: Trip[] }).results;
  return [];
}

/**
 * Arrêter un trip en cours.
 */
export async function stopTrip(
  accessToken: string | undefined,
  tripId: string
): Promise<Trip> {
  const client = getClient(accessToken);
  const { data } = await client.post<Trip>(`/api/v1/trips/${tripId}/stop/`);
  return data;
}

/**
 * Historique des positions GPS d'un trip.
 */
export async function fetchTripPositions(
  accessToken: string | undefined,
  tripId: string,
  params?: { page?: number; page_size?: number }
): Promise<TripPosition[]> {
  const client = getClient(accessToken);
  const searchParams = new URLSearchParams();
  if (params?.page != null) searchParams.append("page", String(params.page));
  if (params?.page_size != null) searchParams.append("page_size", String(params.page_size));
  const query = searchParams.toString();
  const url = query
    ? `/api/v1/trips/${tripId}/positions/?${query}`
    : `/api/v1/trips/${tripId}/positions/`;
  const response = await client.get<TripPosition[] | { results: TripPosition[] }>(url);
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: TripPosition[] }).results;
  return [];
}
